import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/site/Logo";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  LogOut,
  Download,
  Users,
  CalendarCheck,
  FileText,
  Trophy,
  Flame,
  RefreshCw,
} from "lucide-react";
import { TIDS } from "@/lib/testIds";
import Seo from "@/components/Seo";
import { ADMIN_TITLE, ADMIN_DESCRIPTION } from "@/content/siteMeta";

const STAT_META = [
  { slug: "total", label: "Total leads", icon: Users },
  { slug: "strategy_calls", label: "Strategy calls", icon: CalendarCheck },
  { slug: "checklist_downloads", label: "Checklist downloads", icon: FileText },
  { slug: "won", label: "Won", icon: Trophy },
  { slug: "last_7_days", label: "Last 7 days", icon: Flame },
];

const STATUS_STYLES = {
  new: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  won: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  lost: "bg-white/5 text-white/50 border-white/15",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Admin() {
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads", { params: { limit: 500 } }),
      ]);
      setStats(s.data);
      setLeads(l.data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      leads.filter(
        (l) =>
          (sourceFilter === "all" || l.source === sourceFilter) &&
          (statusFilter === "all" || (l.status || "new") === statusFilter)
      ),
    [leads, sourceFilter, statusFilter]
  );

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${api.defaults.baseURL}/admin/leads/export`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dental-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div
      data-testid={TIDS.adminDashboard}
      className="min-h-screen bg-[#070B14] text-white"
    >
      <Seo
        title={ADMIN_TITLE}
        description={ADMIN_DESCRIPTION}
        path="/admin"
        noindex
      />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#070B14]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-9 w-auto" />
            <div className="font-display font-semibold tracking-tight">Leads Dashboard</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={load}
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/5 h-9"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              data-testid={TIDS.adminExport}
              onClick={exportCsv}
              className="h-9 rounded-full bg-white/10 hover:bg-white/15 text-white border border-white/10"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button
              data-testid={TIDS.adminLogout}
              onClick={logout}
              className="h-9 rounded-full bg-amber-400 text-[#070B14] hover:bg-amber-300"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {STAT_META.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.slug}
                data-testid={TIDS.adminStatCard(m.slug)}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{m.label}</span>
                  <Icon className="w-4 h-4 text-amber-400" />
                </div>
                <div className="mt-3 font-display text-3xl font-semibold">
                  {stats ? stats[m.slug] ?? 0 : "—"}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-44 bg-white/5 border-white/10 text-white h-10">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f172a] border-white/10 text-white">
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="strategy_call">Strategy call</SelectItem>
              <SelectItem value="lead_magnet">Checklist download</SelectItem>
              <SelectItem value="contact">Contact</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white h-10">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f172a] border-white/10 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-white/40 ml-auto">
            {filtered.length} {filtered.length === 1 ? "lead" : "leads"}
          </span>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50">Received</TableHead>
                <TableHead className="text-white/50">Name</TableHead>
                <TableHead className="text-white/50">Practice</TableHead>
                <TableHead className="text-white/50">Contact</TableHead>
                <TableHead className="text-white/50">Source</TableHead>
                <TableHead className="text-white/50">Budget</TableHead>
                <TableHead className="text-white/50">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16 text-white/40">
                    Loading leads…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16 text-white/40">
                    No leads match these filters yet.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((l) => (
                  <TableRow
                    key={l.id}
                    data-testid={TIDS.adminLeadRow(l.id)}
                    className="border-white/5 hover:bg-white/[0.03]"
                  >
                    <TableCell className="text-white/60 text-sm whitespace-nowrap">
                      {fmtDate(l.created_at)}
                    </TableCell>
                    <TableCell className="font-medium text-white">{l.full_name || "—"}</TableCell>
                    <TableCell className="text-white/70">{l.practice_name || "—"}</TableCell>
                    <TableCell className="text-white/70 text-sm">
                      <div>{l.email}</div>
                      {l.phone && <div className="text-white/40">{l.phone}</div>}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono-accent uppercase tracking-wider text-white/50">
                        {(l.source || "").replace("_", " ")}
                      </span>
                    </TableCell>
                    <TableCell className="text-white/60 text-sm">{l.monthly_ads_budget || "—"}</TableCell>
                    <TableCell>
                      <Select value={l.status || "new"} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger className={`w-32 h-8 text-xs border ${STATUS_STYLES[l.status || "new"]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-white/10 text-white">
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="won">Won</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
